import React, { useCallback, useEffect, useState } from 'react';
import { message } from 'antd';
import { Button, Card, TextField, Typography } from '../../design-system';
import { useSelected } from '../../providers/selectedContext';
import {
  getGheInputs,
  upsertGheInputs
} from '../../api/data';
import {
  GheInputSessionUpsert,
  ReservoirConditions,
  RockId,
  TargetRockInput
} from '../../@types/gheInputs/types';
import { GheTargetRocksForm, TargetRockRow } from './GheTargetRocksForm';
import styles from '../gheShared/gheStickyHeader.module.css';

const ROCK_IDS: RockId[] = [
  'GHE1',
  'GHE2',
  'GHE3',
  'GHE4',
  'GHE5',
  'GHE6',
  'GHE7',
  'GHE8',
  'GHE9',
  'GHE10'
];

const emptyRows = (): TargetRockRow[] =>
  ROCK_IDS.map(rock_id => ({
    rock_id,
    porosity: null,
    permeability_md: null,
    analogues_count: ''
  }));

const emptyConditions: ReservoirConditions = {
  nacl_ppm: 0,
  mgcl2_ppm: 0,
  pressure_mpa: 1,
  temperature_c: 0
};

const CONDITION_FIELDS: {
  key: keyof ReservoirConditions;
  label: string;
  min?: number;
}[] = [
  { key: 'nacl_ppm', label: 'NaCl (ppm)', min: 0 },
  { key: 'mgcl2_ppm', label: 'MgCl₂ (ppm)', min: 0 },
  { key: 'pressure_mpa', label: 'Pressure (MPa)', min: 1 },
  { key: 'temperature_c', label: 'Temperature (°C)' }
];

const toNumber = (raw: string): number => {
  const value = Number(raw);
  return Number.isNaN(value) ? 0 : value;
};

export const GheInputsTab = ({
  actions,
  onSaved,
  hideHeader = false
}: {
  actions?: React.ReactNode;
  onSaved?: () => void;
  hideHeader?: boolean;
}) => {
  const { selectedInfo } = useSelected();
  const projectUid = selectedInfo.project?.uid;
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState<TargetRockRow[]>(emptyRows());
  const [conditions, setConditions] = useState<ReservoirConditions>(
    emptyConditions
  );

  const loadInputs = useCallback(async () => {
    if (!projectUid) return;
    setLoading(true);
    try {
      const session = await getGheInputs(projectUid);
      if (session) {
        const base = emptyRows();
        const mapped = base.map(row => {
          const match = session.target_rocks.find(
            rock => rock.rock_id === row.rock_id
          );
          if (!match) return row;
          return {
            rock_id: row.rock_id,
            porosity: match.porosity,
            permeability_md: match.permeability_md,
            analogues_count: match.analogues_count
          };
        });
        setRows(mapped);
        setConditions(session.reservoir_conditions);
      } else {
        setRows(emptyRows());
        setConditions(emptyConditions);
      }
    } catch (error) {
      message.error('Failed to load GHE inputs');
    } finally {
      setLoading(false);
    }
  }, [projectUid]);

  useEffect(() => {
    loadInputs();
  }, [loadInputs]);

  const normalizeTargetRocks = useCallback((): TargetRockInput[] => {
    const populated = rows.filter(
      row => row.porosity !== null || row.permeability_md !== null
    );

    const hasIncomplete = populated.some(
      row => row.porosity === null || row.permeability_md === null
    );

    if (hasIncomplete) {
      throw new Error(
        'Each target rock requires both porosity and permeability values.'
      );
    }

    return populated.map(row => {
      let analogues = row.analogues_count;
      if (analogues === '' || analogues === null || analogues === undefined) {
        analogues = 'auto';
      }
      if (typeof analogues === 'string' && analogues !== 'auto') {
        const asNumber = Number(analogues);
        if (Number.isNaN(asNumber)) {
          throw new Error('Analogues count must be a number or "auto".');
        }
        analogues = asNumber;
      }
      return {
        rock_id: row.rock_id,
        porosity: row.porosity as number,
        permeability_md: row.permeability_md as number,
        analogues_count: analogues
      };
    });
  }, [rows]);

  const validateConditions = useCallback(() => {
    if (conditions.pressure_mpa <= 0) {
      throw new Error('Pressure must be greater than 0.');
    }
    if (conditions.nacl_ppm < 0 || conditions.mgcl2_ppm < 0) {
      throw new Error('Salinity values must be non-negative.');
    }
  }, [conditions]);

  const handleSave = async () => {
    if (!projectUid) return;
    try {
      setSaving(true);
      const target_rocks = normalizeTargetRocks();
      validateConditions();
      const payload: GheInputSessionUpsert = {
        target_rocks,
        reservoir_conditions: conditions
      };
      await upsertGheInputs(projectUid, payload);
      message.success('GHE inputs saved');
      await loadInputs();
      onSaved?.();
    } catch (error: any) {
      const apiMessage =
        error?.response?.data?.message ||
        error?.response?.data?.detail?.[0]?.msg ||
        error?.message;
      message.error(apiMessage || 'Failed to save GHE inputs');
    } finally {
      setSaving(false);
    }
  };

  const body = (
    <>
      <Card fullWidth padding="none">
        <Card.Header title="Reservoir Conditions" />
        <Card.Body style={{ padding: 'var(--ds-space-lg) 20px' }}>
          <div style={{ display: 'flex', flexDirection: 'row', gap: '1rem' }}>
            {CONDITION_FIELDS.map(field => (
              <TextField
                key={field.key}
                label={field.label}
                type="number"
                min={field.min}
                fullWidth
                disabled={loading}
                value={conditions[field.key]}
                onChange={event =>
                  setConditions(prev => ({
                    ...prev,
                    [field.key]: toNumber(event.target.value)
                  }))
                }
              />
            ))}
          </div>
        </Card.Body>
      </Card>
      <Card fullWidth padding="none">
        <Card.Header title="Target Rocks" />
        <Card.Body style={{ padding: 0 }}>
          <GheTargetRocksForm rows={rows} onChange={setRows} loading={loading} />
        </Card.Body>
      </Card>
      {/* Packaged IFT artifacts are resolved server-side; no separate activation is required here. */}
      <div style={{ textAlign: 'right' }}>
        <Button variant="primary" onClick={handleSave} loading={saving} disabled={loading}>
          Save Inputs
        </Button>
      </div>
    </>
  );

  // Embedded (split screen): the parent pane provides the flex column + gap,
  // so we skip the extra .tab/.content wrappers.
  if (hideHeader) {
    return body;
  }

  return (
    <div className={styles.tab}>
      <div className={styles.stickyHeader}>
        <Typography.Heading level={3} style={{ margin: 0 }}>
          GHE Inputs
        </Typography.Heading>
        {actions}
      </div>
      <div className={styles.content}>{body}</div>
    </div>
  );
};
